import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Boxes, Folder, Hash, Loader, MessageSquare, Search } from 'lucide-react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import { useChatStore } from '../store/chatStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import chatService from '../services/chatService';
import { ensureDm } from '../utils/chatOpen';
import { channelDisplay, dmOther, initials, avatarColor, isCompleteEmail } from './chatHelpers';
import UserAvatar from './ui/UserAvatar';
import { setActiveTab } from '../store/uiModalStore';
import {
  shareToChannel,
  buildSnapshotPayload,
  assertSnapshotSize,
  hydrateWorkspaceSnapshotItem,
} from '../utils/chatShare';
import { findUnredactableRisks } from '../utils/shareRedaction';
import { encryptSnapshotPayload } from '../utils/shareEnvelope';
import ShareCredentialsToggle from './ShareCredentialsToggle';
import { ShareRequestRow } from './ShareMessageCard';
import './Chat.css';

const TYPE_LABELS = {
  workspace: 'Workspace',
  collection: 'Collection',
  request: 'Request',
};

function newUid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * "Share → In Chat" recipient picker: pick an existing conversation or search
 * for a person (a DM is created on the fly), then send the item as a
 * recipient-scoped share card. The share link is minted only AFTER the
 * conversation is chosen — the token must be scoped to that channel.
 */
export default function ShareToChatModal({ isOpen, onClose, target, showToast }) {
  const channels = useChatStore((s) => s.channels);
  const currentUserId = useChatStore((s) => s.currentUserId);
  const setActiveChannel = useChatStore((s) => s.setActiveChannel);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(false);
  // Off by default — see ShareCredentialsToggle for why, and for the copy.
  const [includeCredentials, setIncludeCredentials] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState(null); // { channelId, name }
  const debounce = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setResults([]);
    setError('');
    setSending(false);
    setSentTo(null);
    // A decision made about one item must not silently carry to the next.
    setIncludeCredentials(false);
  }, [isOpen, target]);

  // People lookup is exact-email only (server enforces the same): the lookup
  // fires only once a complete address is typed — no directory browsing.
  useEffect(() => {
    if (!isOpen) return undefined;
    clearTimeout(debounce.current);
    if (!isCompleteEmail(query)) {
      setResults([]);
      return undefined;
    }
    debounce.current = setTimeout(() => {
      chatService.searchUsers(query.trim()).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [query, isOpen]);

  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)),
    [channels]
  );
  const filteredChannels = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedChannels;
    return sortedChannels.filter((c) =>
      channelDisplay(c, currentUserId).name.toLowerCase().includes(q)
    );
  }, [sortedChannels, query, currentUserId]);

  /**
   * Bodies and scripts are deliberately NOT transformed (shareRedaction R8) — a
   * mangled script runs and fails silently on the recipient's machine. They are
   * named instead, following `findExposedSecretKeys`.
   *
   * Read off the raw sidebar item, which is already in memory. A workspace
   * target has no collection tree until `hydrateWorkspaceSnapshotItem` runs at
   * send time, so it gets no per-request naming — the standing copy still says
   * bodies and scripts travel as-is.
   */
  const risks = useMemo(
    () => (!target || target.type === 'workspace' ? [] : findUnredactableRisks(target.item)),
    [target]
  );

  if (!target) return null;
  const { type, item } = target;
  // Snapshot-ness must come from the workspace the ITEM belongs to, not the
  // active one: a workspace target (from the workspace manager's share modal)
  // can be any workspace, while collection/request targets always come from
  // the active workspace's sidebar. Local/git workspaces have no server rows
  // and are shared as frozen snapshots.
  const targetWorkspace = type === 'workspace' ? item : currentWorkspace;
  const isSnapshot =
    targetWorkspace?.storage === 'local' || targetWorkspace?.storage === 'git';
  const workspaceName = type === 'workspace' ? null : currentWorkspace?.name || null;

  // Core send: mint the channel-scoped link, then deliver the card over REST.
  const deliverTo = async (channel) => {
    let payload = null;
    if (isSnapshot) {
      // A workspace target arrives without its collection tree or variables —
      // hydrate both from the local snapshot store before serializing.
      let snapshotItem = item;
      if (type === 'workspace') {
        snapshotItem = await hydrateWorkspaceSnapshotItem(item);
      }
      // Encrypt BEFORE the size check: the ciphertext is base64, so the bytes
      // the 2 MB server cap applies to are the encrypted ones, not the
      // plaintext ones.
      payload = assertSnapshotSize(
        await encryptSnapshotPayload(
          channel.id,
          type,
          buildSnapshotPayload(type, snapshotItem, { includeCredentials })
        )
      );
    }
    const metadata = await shareToChannel(channel.id, { type, item, workspaceName, payload });
    await chatService.sendMessage(channel.id, {
      uid: newUid(),
      body: '',
      kind: 'share',
      metadata,
    });
    const { name } = channelDisplay(channel, currentUserId);
    setSentTo({ channelId: channel.id, name });
    showToast?.(`Shared to ${name}`, 'success');
  };

  const sendTo = async (channel) => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      await deliverTo(channel);
    } catch (e) {
      setError(e.message || 'Failed to share');
    } finally {
      setSending(false);
    }
  };

  const sendToUser = async (user) => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const channel = await ensureDm(user.id);
      await deliverTo(channel);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not start conversation');
    } finally {
      setSending(false);
    }
  };

  const openChat = () => {
    if (sentTo) {
      setActiveChannel(sentTo.channelId);
      setActiveTab('chat.panel');
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share in Chat"
      size="2xl"
      customIcon={<MessageSquare size={22} className="text-blue-500" />}
    >
      {/* Two columns: WHO it goes to on the LEFT, WHAT is being shared on the
          right (owner's order). The picker leads because it is the thing you
          came here to click; the preview is confirmation of a choice already
          made in the sidebar.

          Swapped in the DOM rather than with CSS `order`, so keyboard tab
          order still matches what the eye sees — a visual-only swap puts the
          first thing you look at second in the tab sequence. The consequence
          is that under 640px, where this collapses to one column, the picker
          is also first; that reads correctly for the same reason.

          Falls back to one column under 640px (`share-modal-split` in
          index.css), where two would be narrower than either side needs. */}
      <div className="share-modal-split">
        <div className="share-modal-col space-y-3">
        {sentTo ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/20 p-3 text-center text-sm font-medium text-green-700 dark:text-green-400">
              ✓ Shared to {sentTo.name}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={openChat}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Open chat
              </button>
              <button
                type="button"
                onClick={onClose}
                className="modern-button-secondary flex-1 justify-center"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Recipient search */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
              <input
                className="modal-input modal-input-no-focus-outline"
                style={{ paddingLeft: 32 }}
                placeholder="Filter conversations, or enter a full email address…"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}

            <div className="max-h-64 overflow-y-auto" style={{ margin: '0 -4px', padding: '0 4px' }}>
              {sending && (
                <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
                  <Loader size={14} className="animate-spin" /> Sending…
                </div>
              )}

              {!sending && (
                <>
                  {/* Exact-email match (new DM) */}
                  {results.map((u) => (
                    <div key={`u-${u.id}`} className="chat-user-result" onClick={() => sendToUser(u)}>
                      <UserAvatar className="chat-avatar chat-avatar--sm" userId={u.id} name={u.name} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email}</div>
                      </div>
                    </div>
                  ))}

                  {/* Existing conversations */}
                  {filteredChannels.length > 0 && (
                    <>
                      {filteredChannels.map((c) => {
                        const { name, seed } = channelDisplay(c, currentUserId);
                        return (
                          <div key={`c-${c.id}`} className="chat-user-result" onClick={() => sendTo(c)}>
                            {c.kind === 'dm' ? (
                              <UserAvatar
                                className="chat-avatar chat-avatar--sm"
                                userId={dmOther(c, currentUserId)?.user_id}
                                name={name}
                              />
                            ) : (
                              <div className="chat-avatar chat-avatar--sm" style={{ background: avatarColor(seed) }}>
                                {c.kind === 'channel' ? <Hash size={14} /> : initials(name)}
                              </div>
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                {c.kind === 'dm' ? 'Direct message' : `${(c.members || []).length} members`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {filteredChannels.length === 0 && results.length === 0 && (
                    <div className="py-3 text-sm text-gray-400 dark:text-zinc-500">
                      {isCompleteEmail(query)
                        ? 'No user found with that email address.'
                        : 'No matching conversation — enter a full email address to reach someone new.'}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
        </div>

        <div className="share-modal-col space-y-3">
        {/* What's being shared — rendered the way it looks in the sidebar */}
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 px-3 py-2">
          {type === 'request' ? (
            <ShareRequestRow method={item?.method} name={item?.name} />
          ) : (
            <div className="chat-share-req-row">
              {type === 'workspace' ? (
                <Boxes size={14} className="chat-share-card-typeicon" />
              ) : (
                <Folder size={14} className="chat-share-card-typeicon" />
              )}
              <span className="chat-share-card-name">{item?.name || 'Untitled'}</span>
            </div>
          )}
          {/* OsScroll, like the chat card: `.chat-share-card-tree` bounds the
              height but no longer sets `overflow-y` itself (OsScroll owns it
              there), so this list needs its own scroller or a long collection
              paints straight over the rest of the modal. */}
          {type === 'collection' && (item?.requests || []).length > 0 && (
            <OsScroll className="chat-share-card-tree" defer>
              {(item.requests || []).slice(0, 30).map((r, i) => (
                <ShareRequestRow key={r.id ?? i} method={r.method} name={r.name || r.url} />
              ))}
              {(item.requests || []).length > 30 && (
                <div className="chat-share-card-more">+{(item.requests || []).length - 30} more</div>
              )}
            </OsScroll>
          )}
          <div className="text-xs text-gray-400 dark:text-zinc-500" style={{ marginTop: 4 }}>
            {TYPE_LABELS[type] || type}
            {isSnapshot ? ' · shared as snapshot' : ''}
            {' · '}only the recipient can access it
          </div>
        </div>

        {/* Snapshot shares carry the actual request data, so they are the only
            ones where this choice exists. A cloud share sends a link, and the
            recipient reads the server's copy under its own permissions. */}
        {isSnapshot && !sentTo && (
          <ShareCredentialsToggle
            includeCredentials={includeCredentials}
            onChange={setIncludeCredentials}
            risks={risks}
          />
        )}

        </div>
      </div>
    </Modal>
  );
}
